<div class="flex flex-col gap-xl" id="reflection_list">
  @if (!readonly()) {
    <div class="flex justify-end">
      <lib-button
        id="reflection_add"
        [label]="'reflection_list_add' | transloco"
        [btnClass]="'py-sm px-lg text-sm rounded-md text-white bg-primary-500 font-medium border-none'"
        (click)="add()" />
    </div>
  }

  @if (loadFailed()) {
    <div
      id="reflection_list_error"
      role="alert"
      class="flex items-center justify-between gap-md rounded-lg border border-destructive-300 bg-destructive-50 p-md text-sm text-destructive-700">
      <span>{{ 'reflection_list_load_error' | transloco }}</span>
      <button
        type="button"
        id="reflection_list_retry"
        class="rounded-md border border-destructive-300 bg-white px-md py-xs font-medium text-destructive-700"
        (click)="retry()">
        {{ 'reflection_list_retry' | transloco }}
      </button>
    </div>
  } @else if (!loading() && !groups().length) {
    <p
      id="reflection_list_empty"
      class="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-lg text-center text-sm text-neutral-500">
      {{ 'reflection_list_empty' | transloco }}
    </p>
  } @else {
    @for (group of groups(); track group.event) {
      <section
        class="flex flex-col gap-md"
        [id]="'reflection_group_' + group.event">
        <h3 class="text-sm font-semibold text-neutral-700">
          {{ group.label }}
        </h3>

        @for (item of group.predefined; track $index) {
          <div
            class="flex flex-col gap-xs rounded-lg border border-neutral-200 bg-neutral-50 p-md"
            [class.opacity-60]="item.unavailable"
            [id]="'reflection_predefined_' + group.event + '_' + $index">
            <div class="flex flex-wrap items-center gap-sm">
              <span class="font-medium text-neutral-900">{{ item.name }}</span>
              <lib-badge
                [label]="'reflection_predefined_badge' | transloco"
                [badgeClass]="'bg-neutral-100 text-neutral-700 border-none px-sm text-xs font-medium'" />
              @if (item.unavailable) {
                <lib-badge
                  [label]="'reflection_unavailable_badge' | transloco"
                  [badgeClass]="'bg-warning-50 text-warning-700 border-none px-sm text-xs font-medium'" />
              }
            </div>
            @if (item.actionLabel) {
              <span class="text-sm text-neutral-500">{{
                item.actionLabel
              }}</span>
            }
            @if (item.configSummary) {
              <span class="text-sm text-neutral-500">{{
                item.configSummary
              }}</span>
            }
          </div>
        }

        <div
          cdkDropList
          class="flex flex-col gap-md"
          [cdkDropListDisabled]="readonly() || busy()"
          (cdkDropListDropped)="drop($event, group)">
          @for (card of group.dynamic; track card.id) {
            <div
              cdkDrag
              cdkDragLockAxis="y"
              [cdkDragDisabled]="readonly() || busy()"
              class="flex items-start gap-md rounded-lg border bg-white p-md"
              [class.border-neutral-200]="!card.invalid"
              [class.border-warning-300]="card.invalid"
              [class.opacity-60]="!card.isActive"
              [id]="'reflection_card_' + card.id">
              @if (!readonly()) {
                <button
                  type="button"
                  cdkDragHandle
                  [id]="'reflection_drag_' + card.id"
                  class="cursor-grab select-none px-xs text-neutral-400"
                  [attr.aria-label]="'reflection_drag_handle' | transloco">
                  ⋮⋮
                </button>
              }

              <div class="flex min-w-0 flex-1 flex-col gap-xs">
                <div class="flex flex-wrap items-center gap-sm">
                  <span class="font-medium text-neutral-900">{{
                    card.name
                  }}</span>
                  @if (card.conditionBadge) {
                    <lib-badge
                      [label]="card.conditionBadge"
                      [badgeClass]="'bg-primary-50 text-primary-700 border-none px-sm text-xs font-medium'" />
                  }
                  @if (card.invalid) {
                    <lib-badge
                      [label]="'reflection_invalid_badge' | transloco"
                      [badgeClass]="'bg-warning-50 text-warning-700 border-none px-sm text-xs font-medium'" />
                  }
                  @if (!card.isActive) {
                    <lib-badge
                      [label]="'reflection_inactive_badge' | transloco"
                      [badgeClass]="'bg-neutral-100 text-neutral-700 border-none px-sm text-xs font-medium'" />
                  }
                </div>
                <span class="text-sm text-neutral-500">{{
                  card.actionLabel
                }}</span>
                @if (card.configSummary) {
                  <span class="text-sm text-neutral-500">{{
                    card.configSummary
                  }}</span>
                }
                @if (card.conditionSummary) {
                  <span class="truncate text-sm text-neutral-500">{{
                    card.conditionSummary
                  }}</span>
                }
                @if (card.invalid && card.invalidReasons.length) {
                  <ul
                    class="list-disc ps-xl text-sm text-warning-700"
                    [id]="'reflection_invalid_' + card.id">
                    @for (reason of card.invalidReasons; track $index) {
                      <li>{{ reason }}</li>
                    }
                  </ul>
                }
              </div>

              <div class="flex flex-shrink-0 items-center gap-sm">
                @if (!readonly()) {
                  <button
                    type="button"
                    role="switch"
                    [id]="'reflection_toggle_' + card.id"
                    class="relative h-5 w-9 rounded-full border-none transition-colors"
                    [class.bg-primary-500]="card.isActive"
                    [class.bg-neutral-300]="!card.isActive"
                    [attr.aria-checked]="card.isActive"
                    [attr.aria-label]="'reflection_active_toggle' | transloco"
                    [disabled]="busy()"
                    (click)="toggleActive(card.reflection)">
                    <span
                      class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
                      [class.start-0.5]="!card.isActive"
                      [class.start-[1.125rem]]="card.isActive"></span>
                  </button>
                }
                <button
                  type="button"
                  [id]="'reflection_history_' + card.id"
                  class="rounded-md border border-neutral-300 bg-white px-md py-xs text-sm text-neutral-700"
                  (click)="showHistory(card.reflection)">
                  {{ 'reflection_history' | transloco }}
                </button>
                @if (!readonly()) {
                  <button
                    type="button"
                    [id]="'reflection_edit_' + card.id"
                    class="rounded-md border border-neutral-300 bg-white px-md py-xs text-sm text-neutral-700"
                    (click)="edit(card.reflection)">
                    {{ 'reflection_edit' | transloco }}
                  </button>
                  <button
                    type="button"
                    [id]="'reflection_delete_' + card.id"
                    class="rounded-md border border-destructive-300 bg-white px-md py-xs text-sm text-destructive-700"
                    [disabled]="busy()"
                    (click)="remove(card.reflection)">
                    {{ 'reflection_delete' | transloco }}
                  </button>
                }
              </div>
            </div>
          }
        </div>
      </section>
    }
  }
</div>

@for (view of panelView(); track view.key) {
  <lib-custom-workflow-drawer
    [title]="view.title"
    [wide]="view.kind === PanelKind.EDITOR"
    (closeRequested)="closePanel()">
    @if (view.kind === PanelKind.EDITOR) {
      <lib-reflection-editor
        [flowTemplateId]="flowTemplateId()"
        [scope]="scope()"
        [stepTemplateId]="stepTemplateId()"
        [reflection]="view.reflection"
        [fields]="fields()"
        (saved)="onSaved()"
        (cancelled)="closePanel()" />
    } @else if (view.reflection) {
      <lib-reflection-run-history
        [flowTemplateId]="flowTemplateId()"
        [reflectionId]="view.reflection.id" />
    }
  </lib-custom-workflow-drawer>
}

<ng-template #confirmDelete>
  <div class="mb-sm text-center text-lg font-medium text-neutral-900">
    {{ 'reflection_delete_confirm_title' | transloco }}
  </div>
  <div class="text-center text-sm text-neutral-500">
    {{ 'reflection_delete_confirm_description' | transloco }}
  </div>
  <div class="mt-4xl flex flex-col gap-md">
    <button
      type="button"
      id="reflection_delete_keep"
      class="cursor-pointer rounded-md border-none bg-primary-500 px-2xl py-sm text-lg font-medium text-white"
      (click)="cancelRemove()">
      {{ 'reflection_delete_keep_button' | transloco }}
    </button>
    <button
      type="button"
      id="reflection_delete_confirm"
      class="cursor-pointer rounded-md border border-destructive-300 bg-white px-2xl py-sm text-lg font-medium text-destructive-700"
      (click)="confirmRemove()">
      {{ 'reflection_delete_confirm_button' | transloco }}
    </button>
  </div>
</ng-template>
